import { Button, Icon, Menu, MenuButton, MenuDivider, MenuItem, MenuList, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaGlobe, FaInbox, FaPaperPlane, FaPlus, FaShoppingBasket } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useCreateBasicSourcingEventModal } from "../../../../pages/sourcing-events/buyer/useCreateBasicSourcingEventModal";
import { useCreateSourcingEventMutation } from "../../../../autogen/bff-api";
import { urls } from "../../../../urls";
import { useLoggedInWithOrgContextState } from "../../../auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../subscription/useSub";
import { Explanation } from "../../../support/Explanation/Explanation";

export const Sourcing = () => {
  const navigate = useNavigate();
  const authState = useLoggedInWithOrgContextState();
  const { showCreateBasicSourcingEventModal, createBasicSourcingEventModal } = useCreateBasicSourcingEventModal();
  const { t } = useTranslation();
  const toast = useToast();
  const sub = useSub();

  const canCreateEvent = authState.selectedOrg.canCreateEvent;
  const [createPublicEvent, { isLoading: isCreatingPublicEvent }] = useCreateSourcingEventMutation();

  const handleCreatePublicEvent = useCallback(async () => {
    try {
      const result = await createPublicEvent({ createSourcingEventV2Request: { title: "" } }).unwrap();
      navigate(urls.publicEvents.edit.go(result.id).info);
    } catch {
      toast({ title: t("Could not create event"), status: "error", duration: 3000 });
    }
  }, [createPublicEvent, navigate, t, toast]);

  return (
    <>
      {createBasicSourcingEventModal}
      {sub.hasBuyerAccess && (
        <>
          <Menu>
            <MenuButton
              as={Button}
              leftIcon={<Icon as={FaShoppingBasket} w="15px" h="15px" />}
              colorScheme="teal"
              variant="ghost"
              size={"sm"}
            >
              {t("Sourcing")}
            </MenuButton>
            <MenuList>
              <Explanation
                enabled={!canCreateEvent}
                placement={"left-start"}
                text={t("Only admins and creators can create new events")}
              >
                <MenuItem
                  isDisabled={!canCreateEvent}
                  icon={<Icon as={FaPlus} w="15px" h="15px" marginBottom={-0.5} />}
                  onClick={showCreateBasicSourcingEventModal}
                >
                  {t("New event")}
                </MenuItem>
              </Explanation>
              {sub.hasSourcingEventV2Access && (
                <Explanation
                  enabled={!canCreateEvent}
                  placement={"left-start"}
                  text={t("Only admins and creators can create new events")}
                >
                  <MenuItem
                    isDisabled={!canCreateEvent || isCreatingPublicEvent}
                    icon={<Icon as={FaGlobe} w="15px" h="15px" marginBottom={-0.5} />}
                    onClick={handleCreatePublicEvent}
                  >
                    {t("New public procurement")}
                  </MenuItem>
                </Explanation>
              )}
              {sub.hasSourcingEventV2Access && <MenuDivider />}
              <MenuItem
                icon={<Icon as={FaShoppingBasket} w="15px" h="15px" marginBottom={-0.5} />}
                onClick={() => {
                  navigate(urls.events.viewAll);
                }}
              >
                {t("All sourcing events")}
              </MenuItem>
            </MenuList>
          </Menu>
          <Menu>
            <MenuList>
              <MenuItem
                icon={<Icon as={FaPaperPlane} w="15px" h="15px" marginBottom={-0.5} />}
                onClick={() => {
                  navigate(urls.bids.viewAll);
                }}
              >
                {t("Delivered bids")}
              </MenuItem>
              <MenuItem
                icon={<Icon as={FaInbox} w="15px" h="15px" marginBottom={-0.5} />}
                onClick={() => {
                  navigate(urls.bids.received);
                }}
              >
                {t("Received bids")}
              </MenuItem>
            </MenuList>
          </Menu>
        </>
      )}
      {!sub.hasBuyerAccess && (
        <>
          <Menu>
            <MenuButton
              as={Button}
              leftIcon={<Icon as={FaShoppingBasket} w="15px" h="15px" />}
              colorScheme="teal"
              variant="ghost"
              size={"sm"}
            >
              {t("Bids")}
            </MenuButton>
            <MenuList>
              <MenuItem
                icon={<Icon as={FaInbox} w="15px" h="15px" marginBottom={-0.5} />}
                onClick={() => navigate(urls.events.viewInvitations)}
              >
                {t("Bid invitations")}
              </MenuItem>
              <MenuItem
                icon={<Icon as={FaPaperPlane} w="15px" h="15px" marginBottom={-0.5} />}
                onClick={() => navigate(urls.bids.viewAll)}
              >
                {t("Delivered bids")}
              </MenuItem>
            </MenuList>
          </Menu>
        </>
      )}
    </>
  );
};
